/**
 * Pieces shared by the lab insights card and pages: the words for concepts,
 * outcomes and patterns, chips, and the screenshots a report cites (fetched
 * with the staff sign-in, shown on request).
 */
import React, { useEffect, useState } from 'react';
import { Image as ImageIcon } from 'lucide-react';
import { labInsights } from '../services/api';
import { Dialog, DialogHeader, DialogBody, DialogFooter } from './Dialog';

export const CONCEPT_LABELS = {
  syntax: 'Syntax', 'names-and-scope': 'Names and scope', 'methods-and-functions': 'Methods and functions', types: 'Types',
  variables: 'Variables', conditionals: 'Conditions', loops: 'Loops', 'arrays-and-loops': 'Arrays and loop bounds', strings: 'Strings',
  'objects-and-references': 'Objects and null references', 'classes-and-oop': 'Classes and objects', recursion: 'Recursion',
  'pointers-and-memory': 'Pointers and memory', 'input-and-output': 'Input and output', arithmetic: 'Arithmetic',
  'libraries-and-imports': 'Libraries and imports', 'program-structure': 'Program structure', 'debugging-strategy': 'Debugging strategy',
  'problem-understanding': 'Understanding the task', 'environment-and-tools': 'The IDE and tools', other: 'Other',
};

export const VERDICTS = {
  smooth: { label: 'Went smoothly', color: 'var(--accent-success)' },
  'some-students-struggled': { label: 'Some students struggled', color: 'var(--accent-warning)' },
  'widespread-difficulty': { label: 'Widespread difficulty', color: 'var(--accent-danger)' },
};

export const WORK_PATTERNS = {
  steady: 'Steady', bursty: 'In bursts', 'late-start': 'Late start', 'stop-and-go': 'Stop and go',
  'mostly-idle': 'Mostly idle', 'insufficient-data': 'Too little recorded',
};

export const SCENE_ACTIVITIES = {
  writing: 'Writing', reading: 'Reading', running: 'Running', 'fixing-an-error': 'Fixing an error',
  debugging: 'Debugging', stuck: 'Stuck', away: 'Away', other: 'Other',
};

export const errorMessage = (error, fallback) => error?.response?.data?.error || error?.message || fallback;
export const whenOf = (value) => (value ? new Date(value).toLocaleString([], { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '');
export const timeOf = (value) => (value ? new Date(value).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '');

export function Chip({ children, color = 'var(--text-secondary)', title }) {
  return (
    <span title={title} style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: 999, fontSize: 12, fontWeight: 500, whiteSpace: 'nowrap',
      color, background: `color-mix(in srgb, ${color} 14%, transparent)`,
    }}>{children}</span>
  );
}

export function ConceptChip({ concept }) {
  return <Chip color="var(--accent-purple)">{CONCEPT_LABELS[concept] || CONCEPT_LABELS.other}</Chip>;
}

/**
 * A screenshot a report cites (S…) or a scene of the story (T…): a button,
 * then the image in a dialog. Fetched only when asked for: each comes from
 * the lab's snapshot storage.
 */
export function EvidenceImage({ labId, runId, studentInsightId, evidenceId, label, when }) {
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    if (!open) return undefined;
    let active = true;
    let objectUrl = null;
    setError(null);
    labInsights.image(labId, runId, studentInsightId, evidenceId)
      .then((blob) => { if (!active) return; objectUrl = URL.createObjectURL(blob); setUrl(objectUrl); })
      .catch(async (err) => {
        let message = errorMessage(err, 'Could not load the screenshot.');
        // A Blob error body: read its message.
        if (err?.response?.data instanceof Blob) {
          try { message = JSON.parse(await err.response.data.text()).error || message; } catch { /* keep the generic message */ }
        }
        if (active) setError(message);
      });
    return () => { active = false; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [open, labId, runId, studentInsightId, evidenceId]);
  const titleId = `evidence-${evidenceId}`;
  return (
    <>
      <button type="button" className="btn btn-secondary btn-sm" onClick={() => { setUrl(null); setOpen(true); }}
        aria-label={`Show screenshot ${evidenceId}${when ? ` at ${timeOf(when)}` : ''}`}>
        <ImageIcon size={14} aria-hidden="true" /> {label || evidenceId}
      </button>
      {open && (
        <Dialog size="xl" labelledBy={titleId} onClose={() => setOpen(false)} confirmOnClose={false}>
          <DialogHeader id={titleId} title={`${evidenceId}${when ? ` · ${timeOf(when)}` : ''}`} onClose={() => setOpen(false)} />
          <DialogBody>
            {error && <p role="alert" style={{ color: 'var(--accent-danger)' }}>{error}</p>}
            {!error && !url && <p role="status">Loading the screenshot…</p>}
            {url && <img src={url} alt={`The student's screen${when ? ` at ${timeOf(when)}` : ''}`} style={{ width: '100%', borderRadius: 6 }} />}
          </DialogBody>
          <DialogFooter><button type="button" className="btn btn-secondary" onClick={() => setOpen(false)}>Close</button></DialogFooter>
        </Dialog>
      )}
    </>
  );
}

/** A small labelled number. */
export function Stat({ label, value, hint }) {
  return (
    <div style={{ padding: '10px 12px', borderRadius: 8, background: 'var(--bg-tertiary)', minWidth: 120 }}>
      <div style={{ fontSize: 20, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{value ?? '—'}</div>
      <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{label}</div>
      {hint && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{hint}</div>}
    </div>
  );
}
